// uploadWoredaReport.tsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef } from "react";
import { updateDistributedTowerData } from "services/legalAwarenessService";
import { useTranslation } from "react-i18next";

interface uploadWoredaReportProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    woredaId?: number;
    woredaName?: string;
}

const UploadWoredaReport: React.FC<uploadWoredaReportProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
    woredaId,
    woredaName,
}) => {
    const { t } = useTranslation();
    const [reportSummary, setReportSummary] = useState("");
    const [reportFile, setReportFile] = useState<File | null>(null);
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);

    const modalContentRef = useRef<HTMLDivElement>(null);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!reportSummary.trim()) {
            showToast(t("PleaseProvideReportSummary") || "Please provide a report summary.", "error");
            return;
        }

        if (!reportFile) {
            showToast(t("PleaseSelectReportFile") || "Please select a report file.", "error");
            return;
        }

        setSubmitting(true);

        try {
            const data = new FormData();
            data.append("status", "Reported");
            data.append("report_summary", reportSummary);
            data.append("report_files", reportFile);
            data.append("woreda_prosecutor_name", contextData.user.full_name);

            const response = await updateDistributedTowerData(woredaId!, data);
            
            // Update the distributed_to_woreda in form state
            setLegalResearch((prev: any) => {
                const updatedWoredas = prev.distributed_to_woreda?.map((woreda: any) => 
                    woredaId === woreda.id ? { ...woreda, ...response } : woreda
                ) || [];
                return { ...prev, distributed_to_woreda: updatedWoredas };
            });
            
            showToast(t("WoredaReportUploadedSuccess") || "Woreda Report Uploaded Successfully!", "success");
            onClose();
        } catch (error) {
            console.error("Error uploading woreda report:", error);
            showToast(t("FailedToUploadWoredaReport") || "Failed to upload woreda report.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleFileChange = (file: File | null) => {
        setReportFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("UploadWoredaReport") || "Upload Woreda Report"}
                </h2>

                {woredaName && (
                    <p className="text-sm text-gray-600 mb-4">
                        {t("Woreda") || "Woreda"}: <span className="font-semibold">{woredaName}</span>
                    </p>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="reportSummary"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("SummaryOfWoredaReport") || "Summary of Woreda Report*"}
                            </label>
                            <textarea
                                id="reportSummary"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("EnterReportSummaryPlaceholder") || "Enter report summary..."}
                                value={reportSummary}
                                onChange={(e) => setReportSummary(e.target.value)}
                                required
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="reportFile"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("ReportDocument") || "Report Document*"}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>

                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-1/2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-md"
                        >
                            {t("Cancel") || "Cancel"}
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-1/2 bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (t("Uploading") || "Uploading...") : (t("SubmitReport") || "Submit Report")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadWoredaReport;